import React, {
  Fragment,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useHistory } from "react-router-dom";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { toast } from "react-toastify";
import {
  Avatar,
  Paper,
  Typography,
  makeStyles,
  Grid,
  Tooltip,
  Box,
  Chip,
  IconButton,
} from "@material-ui/core";
import {
  ReportProblem as ReportProblemIcon,
  VisibilityOutlined as VisibilityOutlinedIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
} from "@material-ui/icons";
import { format, isSameDay, parseISO } from "date-fns";
import { getBackendUrl } from "../../config";

const backendUrl = getBackendUrl();

const useStyles = makeStyles((theme) => ({
  gridContainer: {
    width: "100%",
    margin: 0,
  },
  gridItem: {
    padding: theme.spacing(1),
  },
  columnCard: {
    height: "100%",
    minHeight: 460,
    maxHeight: "calc(100vh - 200px)",
    display: "flex",
    flexDirection: "column",
    borderRadius: 12,
    backgroundColor: theme.palette.type === "dark" 
      ? "rgba(22, 27, 34, 0.75)" 
      : "#FFFFFF",
    overflow: "hidden",
  },
  columnHeader: {
    padding: "12px 14px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    backgroundColor: theme.palette.type === "dark"
      ? "rgba(255, 255, 255, 0.03)"
      : "rgba(0, 0, 0, 0.02)",
    borderBottom: theme.palette.type === "dark"
      ? "1px solid rgba(255, 255, 255, 0.06)"
      : "1px solid #EDF2F7",
    flexShrink: 0,
  },
  agentInfo: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    minWidth: 0,
    flex: 1,
  },
  agentAvatar: {
    width: 36,
    height: 36,
    fontSize: "0.95rem",
    fontWeight: 700,
    border: theme.palette.type === "dark" ? `1px solid ${theme.palette.primary.main}4D` : "1px solid #CBD5E1",
  },
  pendingAvatar: {
    width: 36,
    height: 36,
    backgroundColor: theme.palette.type === "dark" ? "rgba(245, 158, 11, 0.15)" : "#FEF3C7",
    color: "#F59E0B",
    border: "1px solid rgba(245, 158, 11, 0.3)",
  },
  agentName: {
    fontWeight: 700,
    fontSize: "0.9rem",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    color: theme.palette.type === "dark" ? "#FFFFFF" : "#0F172A",
  },
  pendingTitle: {
    fontWeight: 700,
    fontSize: "0.9rem",
    color: theme.palette.type === "dark" ? "#F59E0B" : "#D97706",
  },
  countChip: {
    height: 22,
    fontSize: "0.75rem",
    fontWeight: 600,
    backgroundColor: theme.palette.type === "dark" ? `${theme.palette.primary.main}20` : "#E0F2FE",
    color: theme.palette.type === "dark" ? theme.palette.primary.main : "#0284C7",
    border: theme.palette.type === "dark" ? `1px solid ${theme.palette.primary.main}4D` : "1px solid #BAE6FD",
  },
  pendingChip: {
    height: 22,
    fontSize: "0.75rem",
    fontWeight: 600,
    backgroundColor: theme.palette.type === "dark" ? "rgba(245, 158, 11, 0.15)" : "#FEF3C7",
    color: "#F59E0B",
    border: "1px solid rgba(245, 158, 11, 0.3)",
  },
  ticketListArea: {
    flex: 1,
    minHeight: 0,
    overflowY: "auto",
    padding: "10px",
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    ...theme.scrollbarStyles,
  },
  ticketCard: {
    backgroundColor: theme.palette.type === "dark"
      ? "rgba(255, 255, 255, 0.035)"
      : "#F8FAFC",
    border: theme.palette.type === "dark"
      ? "1px solid rgba(255, 255, 255, 0.06)"
      : "1px solid #E2E8F0",
    borderRadius: 10,
    padding: "10px 12px",
    display: "flex",
    flexDirection: "column",
    gap: "6px",
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor: theme.palette.type === "dark"
        ? "rgba(255, 255, 255, 0.065)"
        : "#FFFFFF",
      borderColor: `${theme.palette.primary.main}4D`,
      boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
      transform: "translateY(-1px)",
    },
  },
  ticketTopRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "8px",
  },
  contactInfo: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    minWidth: 0,
    flex: 1,
  },
  contactAvatar: {
    width: 28,
    height: 28,
    fontSize: "0.8rem",
    fontWeight: 600,
  },
  contactName: {
    fontWeight: 600,
    fontSize: "0.85rem",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    color: theme.palette.type === "dark" ? "#F1F5F9" : "#0F172A",
  },
  ticketTime: {
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
    whiteSpace: "nowrap",
  },
  ticketTimeUnread: {
    fontSize: "0.75rem",
    fontWeight: 700,
    color: "#10B981",
    whiteSpace: "nowrap",
  },
  messagePreview: {
    fontSize: "0.8rem",
    color: theme.palette.type === "dark" ? "rgba(255, 255, 255, 0.7)" : "#64748B",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    paddingLeft: "36px",
  },
  ticketBottomRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    paddingTop: "2px",
    paddingLeft: "36px",
  },
  badgeGroup: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    flexWrap: "wrap",
    minWidth: 0,
    flex: 1,
  },
  whatsappBadge: {
    height: 18,
    fontSize: "0.65rem",
    fontWeight: 600,
    padding: "0 6px",
    borderRadius: 4,
    backgroundColor: "rgba(16, 185, 129, 0.15)",
    color: "#10B981",
    border: "1px solid rgba(16, 185, 129, 0.3)",
    maxWidth: 120,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  queueBadge: {
    height: 18,
    fontSize: "0.65rem",
    fontWeight: 600,
    padding: "0 6px",
    borderRadius: 4,
    color: "#FFFFFF",
    maxWidth: 100,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  inspectButton: {
    padding: 4,
    color: theme.palette.type === "dark" ? "rgba(255, 255, 255, 0.6)" : "#64748B",
    "&:hover": {
      color: theme.palette.primary.main,
      backgroundColor: `${theme.palette.primary.main}1A`,
    },
  },
  emptyState: {
    padding: "48px 16px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    gap: "8px",
    flex: 1,
  },
  emptyIcon: {
    fontSize: 36,
    color: "#10B981",
    opacity: 0.75,
  },
  emptyText: {
    fontWeight: 600,
    fontSize: "0.85rem",
    color: theme.palette.text.primary,
  },
  emptySubtext: {
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
  },
}));

const DashboardManage = () => {
  const classes = useStyles();
  const history = useHistory();
  const { user, socket } = useContext(AuthContext);

  const [tickets, setTickets] = useState([]);
  const [update, setUpdate] = useState(false);
  const companyId = user?.companyId;

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/usersMoments");
        setTickets(data);
        setUpdate((prev) => !prev);
      } catch (err) {
        if (err.response?.status !== 500) {
          toastError(err);
        } else {
          toast.error(`${i18n.t("frontEndErrors.getUsers")}`);
        }
      }
    })();
  }, []);

  useEffect(() => {
    if (!socket || !companyId) return;

    const onAppMessage = (data) => {
      if (
        data.action === "create" ||
        data.action === "update" ||
        data.action === "delete"
      ) {
        (async () => {
          try {
            const { data } = await api.get("/usersMoments");
            setTickets(data);
            setUpdate((prev) => !prev);
          } catch (err) {
            toastError(err);
          }
        })();
      }
    };

    socket.on(`company-${companyId}-ticket`, onAppMessage);
    socket.on(`company-${companyId}-appMessage`, onAppMessage);

    return () => {
      socket.off(`company-${companyId}-ticket`, onAppMessage);
      socket.off(`company-${companyId}-appMessage`, onAppMessage);
    };
  }, [socket, companyId]);

  const renderTicketCard = (ticket) => {
    const isUnread = Number(ticket.unreadMessages) > 0;
    const updatedAt = ticket.updatedAt ? parseISO(ticket.updatedAt) : new Date();
    const timeFormatted = isSameDay(updatedAt, new Date())
      ? format(updatedAt, "HH:mm")
      : format(updatedAt, "dd/MM/yyyy");

    return (
      <div
        key={ticket.id}
        className={classes.ticketCard}
        onClick={() => history.push(`/tickets/${ticket.uuid}`)}
      >
        <div className={classes.ticketTopRow}>
          <div className={classes.contactInfo}>
            <Avatar
              alt={ticket?.contact?.name}
              src={ticket?.contact?.urlPicture}
              className={classes.contactAvatar}
            >
              {ticket?.contact?.name ? ticket.contact.name.charAt(0).toUpperCase() : "C"}
            </Avatar>
            <Typography className={classes.contactName}>
              {ticket?.contact?.name || "Sin nombre"}
            </Typography>
          </div>
          <Typography className={isUnread ? classes.ticketTimeUnread : classes.ticketTime}>
            {timeFormatted}
          </Typography>
        </div>

        {ticket.lastMessage && (
          <Typography className={classes.messagePreview}>
            {ticket.lastMessage}
          </Typography>
        )}

        <div className={classes.ticketBottomRow}>
          <div className={classes.badgeGroup}>
            {ticket?.whatsapp?.name && (
              <span className={classes.whatsappBadge}>
                {ticket.whatsapp.name}
              </span>
            )}
            <span
              className={classes.queueBadge}
              style={{
                backgroundColor: ticket.queue?.color || "#64748B",
              }}
            >
              {ticket.queue?.name?.toUpperCase() || i18n.t("momentsUser.noqueue")}
            </span>
          </div>

          <Tooltip title={i18n.t("momentsUser.tooltips.accessTicket")}>
            <IconButton
              size="small"
              className={classes.inspectButton}
              onClick={(e) => {
                e.stopPropagation();
                history.push(`/tickets/${ticket.uuid}`);
              }}
            >
              <VisibilityOutlinedIcon style={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </div>
      </div>
    );
  };

  const Moments = useMemo(() => {
    if (!tickets || tickets.length === 0) return null;

    const ticketsByUser = tickets.reduce((userTickets, ticket) => {
      const u = ticket.user;
      if (u) {
        const userIndex = userTickets.findIndex((g) => g.user.id === u.id);
        if (userIndex === -1) {
          userTickets.push({
            user: u,
            userTickets: [ticket],
          });
        } else {
          userTickets[userIndex].userTickets.push(ticket);
        }
      }
      return userTickets;
    }, []);

    return ticketsByUser.map((group) => (
      <Grid item xs={12} sm={6} md={3} key={group.user.id} className={classes.gridItem}>
        <Paper className={classes.columnCard} variant="outlined">
          <div className={classes.columnHeader}>
            <div className={classes.agentInfo}>
              <Avatar
                alt={group.user.name}
                src={
                  group.user.profileImage
                    ? `${backendUrl}/public/company${companyId}/user/${group.user.profileImage}`
                    : null
                }
                className={classes.agentAvatar}
              >
                {group.user.name ? group.user.name.charAt(0).toUpperCase() : "A"}
              </Avatar>
              <Typography className={classes.agentName}>
                {group.user.name}
              </Typography>
            </div>
            <Chip
              size="small"
              className={classes.countChip}
              label={`${group.userTickets?.length || 0} ${i18n.t("momentsUser.ticketsCount")}`}
            />
          </div>

          <div className={classes.ticketListArea}>
            {group.userTickets && group.userTickets.length > 0 ? (
              group.userTickets.map(renderTicketCard)
            ) : (
              <div className={classes.emptyState}>
                <CheckCircleOutlineIcon className={classes.emptyIcon} />
                <Typography className={classes.emptyText}>
                  {i18n.t("momentsUser.noPending")}
                </Typography>
                <Typography className={classes.emptySubtext}>
                  {i18n.t("momentsUser.allCaughtUp")}
                </Typography>
              </div>
            )}
          </div>
        </Paper>
      </Grid>
    ));
  }, [tickets, update, classes, companyId]);

  const MomentsPending = useMemo(() => {
    const pendingTickets = tickets ? tickets.filter((ticket) => !ticket.user) : [];

    return (
      <Grid item xs={12} sm={6} md={3} key="pending-column" className={classes.gridItem}>
        <Paper className={classes.columnCard} variant="outlined">
          <div className={classes.columnHeader}>
            <div className={classes.agentInfo}>
              <Avatar className={classes.pendingAvatar}>
                <ReportProblemIcon style={{ fontSize: 20 }} />
              </Avatar>
              <Typography className={classes.pendingTitle}>
                {i18n.t("momentsUser.pending")}
              </Typography>
            </div>
            <Chip
              size="small"
              className={classes.pendingChip}
              label={`${pendingTickets.length} ${i18n.t("momentsUser.pendingCount")}`}
            />
          </div>

          <div className={classes.ticketListArea}>
            {pendingTickets.length > 0 ? (
              pendingTickets.map(renderTicketCard)
            ) : (
              <div className={classes.emptyState}>
                <CheckCircleOutlineIcon className={classes.emptyIcon} />
                <Typography className={classes.emptyText}>
                  {i18n.t("momentsUser.noPending")}
                </Typography>
                <Typography className={classes.emptySubtext}>
                  {i18n.t("momentsUser.allCaughtUp")}
                </Typography>
              </div>
            )}
          </div>
        </Paper>
      </Grid>
    );
  }, [tickets, update, classes]);

  return (
    <Grid container spacing={2} className={classes.gridContainer}>
      {Moments}
      {MomentsPending}
    </Grid>
  );
};

export default DashboardManage;
